// routes/components/RouteRenderer.tsx
/**
 * RouteRenderer - Dynamic route rendering based on configuration
 *
 * Renders routes from a declarative configuration array, applying
 * the appropriate auth wrapper based on route type.
 * Each route is wrapped in an ErrorBoundary for error isolation.
 */

import { Suspense, ReactElement, ComponentType } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { PageLoading, ErrorBoundary } from '../../components/common';
import { ROUTES } from '../../config/routes';

import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { OpenRoute } from './OpenRoute';
import { OptionalAuthRoute } from './OptionalAuthRoute';
import { PlayerProtectedRoute } from './PlayerProtectedRoute';
import { PlayerPublicRoute } from './PlayerPublicRoute';
import { EntryRedirect } from './EntryRedirect';
import type { RouteConfig, RouteAuth, RouteRendererProps } from '../types';
import type { AccountType, TournamentRole } from '../../types/roles.types';

type WrapperComponent = ComponentType<{
  children: React.ReactNode;
  requiredRoles?: string[];
  requiredAccountTypes?: AccountType[];
  requiredTournamentRoles?: TournamentRole[];
  allowHeadJudgeTeam?: boolean;
  allowActingLead?: boolean;
  allowFloorTeam?: boolean;
}>;

/**
 * Get the appropriate wrapper component for a route
 */
function getWrapper(auth: RouteAuth | undefined): WrapperComponent {
  switch (auth) {
    case 'protected':
      return ProtectedRoute;
    case 'public':
      return PublicRoute;
    case 'open':
      return OpenRoute;
    case 'optional':
      return OptionalAuthRoute;
    case 'player':
      return PlayerProtectedRoute;
    case 'playerPublic':
      return PlayerPublicRoute;
    default:
      return OpenRoute;
  }
}

/**
 * Render a single route with its wrapper
 */
function renderRoute(routeConfig: RouteConfig): ReactElement {
  const {
    path,
    element: Element,
    auth = 'open',
    roles = [],
    accountTypes = [],
    tournamentRoles = [],
    props = {},
    allowHeadJudgeTeam = false,
    allowActingLead = false,
    allowFloorTeam = false,
    entryRedirect = false,
  } = routeConfig;

  const Wrapper = getWrapper(auth);

  // Build wrapper props for ProtectedRoute
  const wrapperProps: {
    requiredRoles?: string[];
    requiredAccountTypes?: AccountType[];
    requiredTournamentRoles?: TournamentRole[];
    allowHeadJudgeTeam?: boolean;
    allowActingLead?: boolean;
    allowFloorTeam?: boolean;
  } = {};

  if (auth === 'protected') {
    wrapperProps.requiredRoles = roles;
    wrapperProps.requiredAccountTypes = accountTypes;
    wrapperProps.requiredTournamentRoles = tournamentRoles;
    wrapperProps.allowHeadJudgeTeam = allowHeadJudgeTeam;
    wrapperProps.allowActingLead = allowActingLead;
    wrapperProps.allowFloorTeam = allowFloorTeam;
  }

  // Entry-point routes (/landing, /player) wrap the page in
  // EntryRedirect inside the auth wrapper so a logged-in user with
  // no picker preference gets bounced to /welcome first.
  const renderedElement = entryRedirect ? (
    <EntryRedirect>
      <Element {...props} />
    </EntryRedirect>
  ) : (
    <Element {...props} />
  );

  return (
    <Route
      key={path}
      path={path}
      element={
        <ErrorBoundary severity="page" resetKey={path}>
          <Wrapper {...wrapperProps}>{renderedElement}</Wrapper>
        </ErrorBoundary>
      }
    />
  );
}

/**
 * RouteRenderer Component
 * Renders all routes from configuration with Suspense wrapper
 * and ErrorBoundary to catch any unhandled errors
 */
export function RouteRenderer({ routes }: RouteRendererProps): ReactElement {
  const location = useLocation();

  return (
    <ErrorBoundary severity="page" resetKey={location.pathname}>
      <Suspense fallback={<PageLoading />}>
        {/*
          AnimatePresence drives the exit half of MotionPage's
          enter/exit transition (which is mounted inside
          MinimalPageContainer). Without this wrapper, exit
          animations defined in motion.div don't fire — React
          tears the old page down before Framer has a chance to
          play the exit. mode='wait' makes the new page wait for
          the old one's exit to finish, which feels deliberate
          and avoids overlapping content during the swap. The
          ~220ms gap is short enough not to feel sluggish.

          Classic-theme pages have no motion.div with exit
          props, so AnimatePresence becomes a no-op for them —
          their unmount is instant, same as before this commit.

          Routes gets location + key={location.pathname} so
          React Router yields a different rendered children
          per path, which is what AnimatePresence needs to
          detect mount/unmount cycles.
        */}
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            {routes.map(renderRoute)}

            {/* Fallback route */}
            <Route path="*" element={<Navigate to={ROUTES.PLAYER_REGISTER} replace />} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </ErrorBoundary>
  );
}

export default RouteRenderer;
